import { checkActingOnBehalfOf, checkActingType, checkAuthnLevel, checkAuthnMethod, } from './acr-check.js';
export class BaJwt {
    constructor(raw) {
        this._raw = raw;
        const ajwt = raw.split('.');
        if (ajwt.length === 3) {
            const decodedJwt = this.decodeJwt(ajwt[1]);
            this._payload = JSON.parse(decodedJwt);
        }
    }
    get raw() {
        return this._raw;
    }
    get payload() {
        return this._payload;
    }
    /**
     * Compare the JWT's authn-level with `a` and return whether it is at least as high.
     * If no authn-level is required (`!a`), true is returned.
     * @param a the required minimum authentication level
     * @returns true if at least this level is present
     */
    hasAtLeastAuthnLevel(a) {
        return checkAuthnLevel(this.effectiveAuthnLevel(), a);
    }
    effectiveAuthnLevel() {
        return this._payload?.['authn-level'];
    }
    /**
     * Compares the JWT's acting-on-behalf-of with the given baforPatterns.
     * If baforPatterns is undefined/null, true is returned.
     * If baforPatterns is empty, the JWT must be silver/bronze with no or empty
     * acting-on-behalf-of, thus return true if acting-on-behalf-of is missing there
     * as well.
     * If baforPatterns is not empty, all the JWT's acting-on-behalf-of (normally just 1)
     * must match at least one of the given baforPatterns.
     * @param baforPrefixes list of BAFOR prefixes in actingOnBehalfOf
     * @returns true if condition fulfilled
     */
    hasMatchingActingOnBehalfOf(baforPrefixes) {
        return checkActingOnBehalfOf(this.effectingActingOnBehalfOf(), baforPrefixes);
    }
    effectingActingOnBehalfOf() {
        return this._payload?.['acting-on-behalf-of'];
    }
    /**
     * Checks whether the JWT's acting-type is listed in af.
     * If af is undefined, this check is skipped and true is returned.
     * If af is empty, the JWT must not have an acting-type (Bronze).
     * @param af the acting-type to compare against
     * @returns true in case of a match
     */
    hasMatchingActingType(af) {
        return checkActingType(this.effectiveActingType(), af);
    }
    /**
     * Checks whether the JWT's amr matches the required authn-method
     * If am is undefined or empty, true is returned
     * @param am
     */
    hasMatchingAuthnMethod(am) {
        return checkAuthnMethod(this.effectiveAuthnMethod(), am);
    }
    effectiveAuthnMethod() {
        return this._payload?.amr ? this._payload.amr : [];
    }
    fulfills(ao) {
        if (ao.authnLevel && !this.hasAtLeastAuthnLevel(ao.authnLevel)) {
            return false;
        }
        if (ao.actingOnBehalfOf && !this.hasMatchingActingOnBehalfOf(ao.actingOnBehalfOf)) {
            return false;
        }
        if (ao.actingType && !this.hasMatchingActingType(ao.actingType)) {
            return false;
        }
        if (ao.authnMethod && !this.hasMatchingAuthnMethod(ao.authnMethod)) {
            return false;
        }
        return true;
    }
    /**
     * Return the expiration date as unix timestamp in seconds
     * (NumericDate in JWT speak).
     * Basically 1:1 the value `exp` from the JWT, except that 0 is returned when this
     * instance has been initialized with a malformed JWT.
     */
    get expirationDate() {
        return this._payload?.exp || 0;
    }
    effectiveActingType() {
        return this._payload?.['acting-type'];
    }
    decodeJwt(base64urlEncoded) {
        const base64NonUrlEncoded = base64urlEncoded
            .replace(/\s+/g, '')
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        const utf8BytesAsString = atob(base64NonUrlEncoded);
        const utf8ByteArray = Uint8Array.from(utf8BytesAsString, (m) => m.codePointAt(0) || 0);
        return new TextDecoder().decode(utf8ByteArray);
    }
}
